import React, { useEffect, useState } from "react";
import { Dropdown, DropdownOptions } from "../ui/Dropdown/Dropdown";
import { CheckCircle, Heart } from "lucide-react";
import { Searchbox, User } from "../ui/Searchbox/Searchbox";
import { MessageBox, Mention, MentionUser } from "../ui/MessageBox/Messagebox";
import { Button } from "../ui/Button/Button";
import { Modal } from "../ui/Modal/Modal";
import Lottie from "lottie-react";
import { useAuth0 } from "@auth0/auth0-react";
import { sendShoutout } from "../../services/shoutoutService";
import detectiveAnimation from "../../assets/animations/detective search.json";
import { Department } from "../../types/department";
import { RecognitionType } from '../../types/recognition';
import { AnimatePresence, motion } from "framer-motion";
import { InsightCards } from "../ui/InsightCard/InsightCard";

export const RecognitionForm = () => {
    const { getAccessTokenSilently } = useAuth0();

    const [selectedRecognition, setSelectedRecognition] = useState<RecognitionType | null>(null);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [message, setMessage] = useState("");
    const [mentions, setMentions] = useState<Mention[]>([]);
    const [mentionableUsers, setMentionableUsers] = useState<MentionUser[]>([]);

    const [isBossModalOpen, setIsBossModalOpen] = useState(false);
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [showInsights, setShowInsights] = useState(false);
    const [lastRecognizedUser, setLastRecognizedUser] = useState<User | null>(null);
    const [lastRecognitionType, setLastRecognitionType] = useState<RecognitionType | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchMentionableUsers = async () => {
            try {
                const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
                const res = await fetch(`${API_BASE_URL}/api/Employees`);
                const data = await res.json();
                setMentionableUsers(data);
            } catch (err) {
                console.error("Failed to load users for mentions", err);
            }
        };
        fetchMentionableUsers();
    }, []);

    useEffect(() => {
        if (!showSuccessToast) {
            return;
        }
        const timer = setTimeout(() => setShowSuccessToast(false), 4000);
        return () => clearTimeout(timer);
    }, [showSuccessToast]);

    useEffect(() => {
        if (!showInsights) {
            return;
        }
        const timer = setTimeout(() => setShowInsights(false), 16000);
        return () => clearTimeout(timer);
    }, [showInsights]);

    const isButtonDisabled =
        !selectedRecognition ||
        !selectedUser ||
        message.trim() === "" ||
        isSubmitting;

    const recognitionOptions: DropdownOptions[] = [
        {
            value: RecognitionType.ShoutOut,
            icon: () => <Heart className="size-4 text-blue-600" />,
            label: <span className="text-gray-800">Shout-out</span>,
        },
    ];

    const getToastTitle = (type: RecognitionType | null) => {
        if (type === RecognitionType.ShoutOut) {
            return "Shout-out";
        }
    };

    const executeShoutout = async () => {
        setIsBossModalOpen(false);
        if (!selectedUser || !message.trim() || !selectedRecognition) {
            return;
        }
        setIsSubmitting(true);
        try {
            const token = await getAccessTokenSilently();

            const payload = {
                receiverId: selectedUser.id,
                message,
                type: selectedRecognition,
                mentionedUserIds: mentions.map(mention => mention.userId),
            };

            await sendShoutout(payload, token);

            setLastRecognizedUser(selectedUser);
            setLastRecognitionType(selectedRecognition);
            setShowSuccessToast(true);
            setShowInsights(true);

            setMessage("");
            setSelectedUser(null);
            setSelectedRecognition(null);
            setMentions([]);
        } catch (error: any) {
            console.error(error);
        }
        finally {
            setIsSubmitting(false);
        }
    };

    const handleSendClick = async () => {
        if (!selectedUser || !selectedRecognition || !message.trim()) {
            return;
        }

        const isExcoShoutout =
            selectedUser.department === Department.Exco &&
            selectedRecognition === RecognitionType.ShoutOut;

        if (isExcoShoutout) {
            setIsBossModalOpen(true);
            return;
        }

        await executeShoutout();
    };

    return (
        <>
            <AnimatePresence>
                {showSuccessToast && lastRecognizedUser && (
                    <motion.div
                        layout
                        initial={{ opacity: 0, y: -50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                        className="fixed top-24 right-4 z-50 flex items-start gap-3 bg-white px-6 py-4 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 max-w-md pointer-events-none"
                    >
                        <div className="mt-1">
                            <CheckCircle className="size-6 text-green-500 fill-green-50" />
                        </div>
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-800 text-[15px]">
                                {getToastTitle(lastRecognitionType)} sent to {lastRecognizedUser.name}!🎉
                            </span>
                            <span className="text-green-600 text-sm mt-0.5">
                                Your recognition has been shared with the team.
                            </span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showInsights && lastRecognitionType && (
                    <InsightCards
                        type={lastRecognitionType}
                        onClose={() => setShowInsights(false)}
                    />
                )}
            </AnimatePresence>

            <Modal isOpen={isBossModalOpen} onClose={() => setIsBossModalOpen(false)}>
                <div className="flex flex-col items-center gap-2 max-w-sm">
                    <Lottie
                        animationData={detectiveAnimation}
                        loop={true}
                        autoplay={true}
                        className="w-24 h-24"
                    />
                    <h2 className="text-2xl font-bold mt-2">A secret mission?</h2>
                    <p className="text-gray-500">We've received intel. You're aiming for the top.</p>
                    <div className="flex gap-4 mt-6">
                        <Button variant="default" className="shadow-md" onClick={() => setIsBossModalOpen(false)}>
                            Abort... 🤫
                        </Button>
                        <Button variant="gradient" onClick={executeShoutout}>
                            Do it anyway! 🚀
                        </Button>
                    </div>
                </div>
            </Modal>

            <div className="space-y-6">
                <div className="flex flex-col md:flex-row gap-6 w-full">
                    <div className="flex-1 bg-white px-8 py-6 rounded-lg border transition-shadow hover:shadow-xl">
                        <h2 className="text-lg font-semibold text-gray-800">Recognition type</h2>
                        <div className="mt-3">
                            <Dropdown
                                placeholder="Choose recognition type"
                                options={recognitionOptions}
                                onSelect={value => setSelectedRecognition(value as RecognitionType)}
                                value={selectedRecognition}
                            />
                        </div>
                    </div>

                    <div className="flex-1 bg-white px-8 py-6 rounded-lg border transition-shadow hover:shadow-xl">
                        <h2 className="text-lg font-semibold text-gray-800">Select Recipient</h2>
                        <div className="mt-3">
                            <Searchbox
                                onSelect={setSelectedUser}
                                value={selectedUser}
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-white px-8 py-4 rounded-lg w-full border transition-shadow hover:shadow-xl text-center">
                    <h2 className="text-lg font-semibold text-gray-800">Message to give the person</h2>
                    <div className="mt-4">
                        <MessageBox
                            placeholder="Write a heartfelt message... type @ to mention someone"
                            value={message}
                            onChange={setMessage}
                            users={mentionableUsers}
                            onMentionsChange={setMentions}
                        />
                    </div>
                </div>

                <div className="relative flex justify-center pt-3">
                    <Button
                        variant="gradient"
                        disabled={isButtonDisabled}
                        onClick={handleSendClick}
                    >
                        <Heart className="size-5" />
                        Send Shout-Out
                    </Button>
                </div>
            </div>
        </>
    );
};

export default RecognitionForm;
